import React, { useEffect, useMemo, useRef, useState } from 'react';
import WorkForm from './WorkForm';
import ProfileEditModal from '../shared/ProfileModal';
import { useConfirm } from '../../UI/Toast';
import type { WorkCollaborator, WorkSubmitMode, WorkVisibility } from '../../../services/postService';
import type { StudioIndustry } from '../../../constants/studio';

interface EditableWork {
    id: string | number;
    type: 'video' | 'image';
    videoUrl?: string;
    images?: string[];
    thumbnailUrl: string;
    title: string;
    description: string;
    tags: string[];
    categories?: string[];
    industry?: StudioIndustry;
    visibility?: WorkVisibility;
    collaborators?: WorkCollaborator[];
}

interface WorkSaveResult {
    postId?: string | number;
}

interface WorkEditModalProps {
    isOpen: boolean;
    onClose: () => void;
    work?: EditableWork;
    onSave: (updatedData: any, submitMode: WorkSubmitMode, currentWork?: EditableWork) => Promise<WorkSaveResult | void>;
    zIndex?: number;
}

const DRAFT_STORAGE_KEY = 'publish_work_draft';
const DRAFT_MAX_AGE_MS = 10 * 60 * 1000; // 10 minutes
const BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT = 'background_work_upload_completed';
const BACKGROUND_WORK_UPLOAD_FAILED_EVENT = 'background_work_upload_failed';

// Build a draft-safe snapshot of form data (excludes File objects which can't be serialized)
const buildDraftSnapshot = (formData: any): any => {
    if (!formData) return null;
    return {
        _savedAt: Date.now(),
        id: '',
        type: formData.workType || 'video',
        videoUrl: formData.videoPreview || '',
        images: formData.imagePreviews || [],
        thumbnailUrl: formData.coverPreview || '',
        title: formData.title || '',
        description: formData.description || '',
        tags: formData.tags || [],
        categories: formData.categories || [],
        industry: formData.industry || undefined,
        visibility: formData.visibility,
        collaborators: formData.collaborators || [],
    };
};

const WorkEditModal: React.FC<WorkEditModalProps> = ({ isOpen, onClose, work, onSave, zIndex }) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMode, setSubmitMode] = useState<WorkSubmitMode | null>(null);
    const [currentWork, setCurrentWork] = useState<EditableWork | undefined>(work);
    const [recoveryFormData, setRecoveryFormData] = useState<any>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const latestFormDataRef = useRef<any>(null);
    const recoveryFormDataRef = useRef<any>(null);
    const recoveryPostIdRef = useRef<string | number | null>(null);
    const { confirm } = useConfirm();

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        // Restore draft from sessionStorage for create mode (no work prop)
        if (!work) {
            if (recoveryFormDataRef.current) {
                setRecoveryFormData(recoveryFormDataRef.current);
                latestFormDataRef.current = recoveryFormDataRef.current;
                setHasUnsavedChanges(true);
                return;
            }

            try {
                const savedDraft = sessionStorage.getItem(DRAFT_STORAGE_KEY);
                if (savedDraft) {
                    const draft = JSON.parse(savedDraft);
                    const age = Date.now() - (draft._savedAt || 0);
                    if (age < DRAFT_MAX_AGE_MS) {
                        setCurrentWork(draft);
                        setRecoveryFormData(null);
                        latestFormDataRef.current = null;
                        setHasUnsavedChanges(true);
                        return;
                    } else {
                        // Stale draft — drop it
                        sessionStorage.removeItem(DRAFT_STORAGE_KEY);
                    }
                }
            } catch (e) {
                console.warn('Failed to restore draft:', e);
                try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
            }
        }

        setCurrentWork(work);
        setRecoveryFormData(null);
        setHasUnsavedChanges(false);
        latestFormDataRef.current = null;
    }, [isOpen, work]);

    // Keep File objects in memory while the background upload runs. sessionStorage
    // preserves text fields across reloads; this ref preserves the actual files so
    // a failed upload can reopen with a fully retryable form in the current tab.
    useEffect(() => {
        const isCurrentRecoveryEvent = (event: Event) => {
            const eventPostId = (event as CustomEvent<{ postId?: string | number }>).detail?.postId;
            return !recoveryPostIdRef.current || !eventPostId || String(eventPostId) === String(recoveryPostIdRef.current);
        };
        const handleCompleted = (event: Event) => {
            if (!isCurrentRecoveryEvent(event)) return;
            try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
            recoveryFormDataRef.current = null;
            recoveryPostIdRef.current = null;
            setRecoveryFormData(null);
        };
        const handleFailed = (event: Event) => {
            if (!isCurrentRecoveryEvent(event)) return;
            if (recoveryFormDataRef.current) {
                setRecoveryFormData(recoveryFormDataRef.current);
                latestFormDataRef.current = recoveryFormDataRef.current;
                setHasUnsavedChanges(true);
            }
        };
        window.addEventListener(BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT, handleCompleted);
        window.addEventListener(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, handleFailed);
        return () => {
            window.removeEventListener(BACKGROUND_WORK_UPLOAD_COMPLETED_EVENT, handleCompleted);
            window.removeEventListener(BACKGROUND_WORK_UPLOAD_FAILED_EVENT, handleFailed);
        };
    }, []);

    const initialFormData = useMemo(() => {
        if (!currentWork) {
            return undefined;
        }

        return {
            workType: currentWork.type,
            videoUrl: currentWork.videoUrl,
            imageUrls: currentWork.images,
            thumbnailUrl: currentWork.thumbnailUrl,
            title: currentWork.title,
            description: currentWork.description,
            tags: currentWork.tags,
            categories: currentWork.categories || [],
            industry: currentWork.industry,
            visibility: currentWork.visibility,
            collaborators: currentWork.collaborators || []
        };
    }, [currentWork]);

    const resolvedInitialFormData = recoveryFormData || initialFormData;

    const buildSavedWork = (formData: any, mode: WorkSubmitMode, saveResult?: WorkSaveResult | void): EditableWork | undefined => {
        const postId = saveResult?.postId ?? currentWork?.id;
        if (!postId) {
            return currentWork;
        }

        return {
            id: postId,
            type: formData.workType,
            videoUrl: formData.videoPreview || currentWork?.videoUrl,
            images: formData.workType === 'image'
                ? (formData.imagePreviews?.length ? formData.imagePreviews : currentWork?.images)
                : undefined,
            thumbnailUrl: formData.coverPreview || currentWork?.thumbnailUrl || '',
            title: formData.title,
            description: formData.description,
            tags: formData.tags || [],
            categories: formData.categories || [],
            industry: formData.industry || undefined,
            visibility: mode === 'draft' ? 'draft' : 'public',
            collaborators: formData.collaborators || currentWork?.collaborators || []
        };
    };

    const handleSubmit = async (
        formData: any,
        mode: WorkSubmitMode,
        options: { closeAfterSave?: boolean } = {}
    ) => {
        setIsSubmitting(true);
        setSubmitMode(mode);

        // Save snapshot BEFORE attempting submission - ensures recovery on failure
        // (including background upload failures after modal has closed)
        if (!work) {
            recoveryFormDataRef.current = formData;
            setRecoveryFormData(formData);
            try {
                sessionStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(buildDraftSnapshot(formData)));
            } catch (e) { console.warn('Failed to save draft snapshot:', e); }
        }

        try {
            const saveResult = await onSave(formData, mode, currentWork);
            recoveryPostIdRef.current = saveResult?.postId ?? currentWork?.id ?? null;
            const savedWork = buildSavedWork(formData, mode, saveResult);
            if (savedWork) {
                setCurrentWork(savedWork);
            }
            setHasUnsavedChanges(false);

            if (mode === 'publish' || options.closeAfterSave) {
                onClose();
            }
        } catch (error) {
            console.error('Failed to save work:', error);
        } finally {
            setIsSubmitting(false);
            setSubmitMode(null);
        }
    };

    const handleRequestClose = async () => {
        if (isSubmitting) {
            return;
        }

        if (!hasUnsavedChanges) {
            onClose();
            return;
        }

        const result = await confirm({
            title: '保存草稿并退出？',
            message: '当前有未保存的修改，是否先保存为草稿再退出？',
            confirmText: '保存并退出',
            cancelText: '直接退出',
            dismissible: false,
            showCloseButton: true
        });

        if (result.action === 'confirm' && latestFormDataRef.current) {
            await handleSubmit(latestFormDataRef.current, 'draft', { closeAfterSave: true });
            return;
        }

        if (result.action === 'cancel') {
            // User explicitly discarded — clear stored draft so it won't re-appear next time
            try { sessionStorage.removeItem(DRAFT_STORAGE_KEY); } catch {}
            recoveryFormDataRef.current = null;
            recoveryPostIdRef.current = null;
            setRecoveryFormData(null);
            onClose();
        }
    };

    // Based on the `work` prop, not `currentWork`. If no `work` was passed in,
    // we're creating a new post — even if we restored draft data into currentWork.
    const isCreateMode = !work;

    return (
        <ProfileEditModal
            isOpen={isOpen}
            onClose={() => void handleRequestClose()}
            title={isCreateMode ? "发布新作品" : "编辑作品"}
            description={isCreateMode ? "上传您的 AI 创作，与社区分享灵感" : "修改作品的基本信息和发布设置"}
            zIndex={zIndex}
        >
            <WorkForm
                mode={isCreateMode ? 'create' : 'edit'}
                initialData={resolvedInitialFormData}
                onSubmit={(formData, mode) => handleSubmit(formData, mode)}
                onCancel={() => void handleRequestClose()}
                isSubmitting={isSubmitting}
                submitMode={submitMode}
                onDirtyChange={setHasUnsavedChanges}
                onDataChange={(data) => {
                    latestFormDataRef.current = data;
                }}
            />
        </ProfileEditModal>
    );
};

export default WorkEditModal;
